const SLABS = [
  { limit: 2000, rate: 0.01 },
  { limit: 48000, rate: 0.005 },
  { limit: 150000, rate: 0.004 },
  { limit: 300000, rate: 0.0025 },
  { limit: 500000, rate: 0.001 },
];

const BLANKET_RATES = { 1: 175.0, 2: 306.0, 3: 437.0 };
const MINIMUM_STANDARD_PREMIUM = 100;
const DAYS_IN_YEAR = 365;

function getMultiYearDiscount(totalYears) {
  if (totalYears >= 10) return 0.3;
  if (totalYears >= 8) return 0.25;
  if (totalYears >= 6) return 0.2;
  if (totalYears >= 2) return 0.15;
  return 0;
}

function calculateSlabPremium(amount, discountRate) {
  let remaining = amount;
  let premium = 0;

  for (const { limit, rate } of SLABS) {
    if (remaining <= 0) break;
    const slabAmount = Math.min(remaining, limit);
    const raw = slabAmount * rate;
    premium += Math.round(raw * (1 - discountRate) * 100) / 100;
    remaining -= slabAmount;
  }

  return premium;
}

function calculateGuardianMinorPremium(amount, dateOfBirth) {
  if (!amount || !dateOfBirth) return 0;

  amount = Number.parseFloat(amount);
  if (Number.isNaN(amount) || amount <= 0) {
    throw new Error("Invalid bond amount");
  }

  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) {
    throw new TypeError("Invalid date of birth");
  }

  const turning18Date = new Date(
    dob.getFullYear() + 18,
    dob.getMonth(),
    dob.getDate()
  );

  let currentDate = new Date();
  let yearIndex = 1;

  const timeTo18 = (turning18Date - currentDate) / (1000 * 60 * 60 * 24 * DAYS_IN_YEAR);
  const totalYears = Math.ceil(timeTo18) - 1;
  const discount = getMultiYearDiscount(totalYears);

  let totalPremium = 0;

  while (currentDate < turning18Date) {
    const appliedDiscount = yearIndex === 1 ? 0 : discount;
    let annualPremium = calculateSlabPremium(amount, appliedDiscount);

    const nextAnniversary = new Date(
      currentDate.getFullYear() + 1,
      currentDate.getMonth(),
      currentDate.getDate()
    );
    const isFinalYear = nextAnniversary > turning18Date;

    if (isFinalYear) {
      const msPerDay = 1000 * 60 * 60 * 24;
      const daysRemaining = Math.ceil((turning18Date - currentDate) / msPerDay);
      const factor = daysRemaining / DAYS_IN_YEAR;
      annualPremium = Math.round(annualPremium * factor * 100) / 100;
    }

    totalPremium += Math.round(annualPremium);
    currentDate = isFinalYear ? turning18Date : nextAnniversary;
    yearIndex++;
  }

  return totalPremium;
}

// Tiered slabs for the standard premium calculator. Each slab applies its
// per-thousand rate up to its `limit`; any remainder falls into the next slab,
// with the trailing slab applying its rate to all remaining amount.
const STANDARD_SLABS = [
  { limit: 2000, ratePerThousand: 10 },
  { limit: 48000, ratePerThousand: 5 },
  { limit: 150000, ratePerThousand: 4 },
  { limit: 300000, ratePerThousand: 2.5 },
  { limit: 500000, ratePerThousand: 1 },
  { limit: Infinity, ratePerThousand: 0.5 },
];

function calculateStandardPremium(amount) {
  let remaining = amount;
  let premium = 0;

  for (const { limit, ratePerThousand } of STANDARD_SLABS) {
    if (remaining <= 0) break;
    const slabAmount = Math.min(remaining, limit);
    premium += (slabAmount * ratePerThousand) / 1000;
    remaining -= slabAmount;
  }

  return Number.parseFloat(Math.max(premium, MINIMUM_STANDARD_PREMIUM).toFixed(2));
}

/**
 * Calculate premium based on bond sub-category.
 *
 * @param {number} amount - Bond amount
 * @param {string} dateOfBirth - DOB string (Guardian - Minor only)
 * @param {string} bondSubCategory - Sub category of the bond
 * @param {number} bondTerm - Term in years (Blanket only)
 * @returns {number} Calculated premium
 */
export const calculatePremium = (amount, dateOfBirth, bondSubCategory, bondTerm) => {
  if (bondSubCategory === "Guardian - Minor") {
    return calculateGuardianMinorPremium(amount, dateOfBirth);
  }

  if (bondSubCategory === "Blanket") {
    return BLANKET_RATES[bondTerm] ?? 0;
  }

  return calculateStandardPremium(amount);
};
